import { useState, useEffect, useRef } from "react"
import {
  Briefcase,
  Clock,
  Phone,
  Download,
  ExternalLink,
  Award,
  Loader2,
} from "lucide-react"
import { Document, Page, pdfjs } from "react-pdf"
import "react-pdf/dist/Page/AnnotationLayer.css"
import "react-pdf/dist/Page/TextLayer.css"

// If you don't use react-pdf in this context, remove the above imports
// and the "Original" tab section below.

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs"

const BASE_URL = "https://hire100x-e1dd4.uc.r.appspot.com"

// ── Design tokens ─────────────────────────────────────────────────────────────
const FF =
  "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
const MONO = "'IBM Plex Mono', monospace"

const T = {
  border: "0.5px solid #e5e7eb",
  radius: 8,
  radiusCard: 10,
  bg: "#fff",
  bgPage: "#f5f5f5",
  bgMuted: "#fafaf9",
  text: "#111827",
  textMuted: "#374151",
  textTertiary: "#6b7280",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
}

// Social icon placeholders (replace with real imports if available)
const SocialIcon = ({ label }) => (
  <div
    style={{
      width: 15,
      height: 15,
      borderRadius: 3,
      background: "#e5e7eb",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <span
      style={{
        fontSize: 8,
        fontWeight: 700,
        color: "#6b7280",
        fontFamily: MONO,
      }}
    >
      {label[0]}
    </span>
  </div>
)

const proxyUrl = (url) =>
  url
    ? `${BASE_URL}/upload-resume/proxy-resume?url=${encodeURIComponent(url)}`
    : null

// ── Fetch helpers ─────────────────────────────────────────────────────────────

// Unified fetch: GET /:orgId/:jobId/applicants?userId= OR ?email=
async function fetchApplicant(
  orgId,
  jobId,
  { applicantId, applicantEmail } = {},
) {
  if (!applicantId && !applicantEmail)
    throw new Error("Must provide applicantId or applicantEmail")
  const param = applicantId
    ? `userId=${encodeURIComponent(applicantId)}`
    : `email=${encodeURIComponent(applicantEmail)}`
  const url = `${BASE_URL}/jobListings/${orgId}/${jobId}/applicants?${param}`
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`)
  const data = await res.json()
  return data.data ?? data
}

async function fetchUserProfile(applicantId) {
  const res = await fetch(`${BASE_URL}/users/${applicantId}`)
  if (!res.ok) throw new Error("User profile not found")
  const data = await res.json()
  if (data.success && data.data) return data.data
  return data
}

// ── Empty / Error state ───────────────────────────────────────────────────────
const NotFound = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      padding: 40,
      textAlign: "center",
      fontFamily: FF,
    }}
  >
    <div
      style={{
        width: 44,
        height: 44,
        borderRadius: T.radiusCard,
        border: T.border,
        background: "#f9fafb",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16,
      }}
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#d1d5db"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="8" r="4" />
        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
      </svg>
    </div>
    <p
      style={{
        fontSize: 13,
        fontWeight: 700,
        color: T.text,
        marginBottom: 6,
        letterSpacing: "-0.01em",
      }}
    >
      Candidate info not present
    </p>
    <p style={{ fontSize: 13, fontWeight: 500, color: T.textTertiary }}>
      No applicant found for the provided details
    </p>
  </div>
)

const LoadingState = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      fontFamily: FF,
    }}
  >
    <div style={{ textAlign: "center" }}>
      <Loader2
        style={{
          width: 20,
          height: 20,
          color: "#a1a1aa",
          animation: "av-spin 0.8s linear infinite",
          margin: "0 auto 8px",
        }}
      />
      <p style={{ fontSize: 12, color: T.textTertiary, fontFamily: MONO }}>
        Loading profile…
      </p>
    </div>
  </div>
)

// ── Main Component ────────────────────────────────────────────────────────────
/**
 * ApplicantViewer
 *
 * Props:
 *   orgId          {string}   — Firestore org id
 *   jobId          {string}   — Firestore job listing id
 *   applicantId    {string?}  — fetch by applicantId (preferred)
 *   applicantEmail {string?}  — fallback: fetch first doc matching email
 *
 * Read-only — no messaging, archiving, or stage changes.
 */
const ApplicantViewer = ({ orgId, jobId, applicantId, applicantEmail }) => {
  const [applicant, setApplicant] = useState(null)
  const [user, setUser] = useState(null)
  const [status, setStatus] = useState("loading") // "loading" | "found" | "not_found"
  const [profileTab, setProfileTab] = useState("parsed")
  const [showAllSkills, setShowAllSkills] = useState(false)
  const [numPages, setNumPages] = useState(null)
  const [containerWidth, setContainerWidth] = useState(700)
  const containerRef = useRef(null)

  // ── PDF container width ───────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return
    const ro = new ResizeObserver(([e]) =>
      setContainerWidth(e.contentRect.width),
    )
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [])

  // ── Data fetch ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgId || !jobId) {
      setStatus("not_found")
      return
    }

    setStatus("loading")
    setApplicant(null)
    setUser(null)

    const run = async () => {
      let resolved = null

      try {
        resolved = await fetchApplicant(orgId, jobId, {
          applicantId,
          applicantEmail,
        })
      } catch (_) {}

      if (!resolved) {
        setStatus("not_found")
        return
      }

      setApplicant(resolved)

      // Fetch rich user profile
      const uid = resolved.applicantId ?? resolved.uid ?? applicantId
      if (uid) {
        try {
          const profile = await fetchUserProfile(uid)
          setUser(profile)
        } catch (_) {
          // graceful — render from applicant doc alone
        }
      }

      setStatus("found")
    }

    run()
  }, [orgId, jobId, applicantId, applicantEmail])

  // ── Derived data ──────────────────────────────────────────────────────────
  const pdfWidth = Math.min(containerWidth - 48, 860)
  const resumeUrl = proxyUrl(applicant?.resume?.resumeUrl)

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        @keyframes av-spin { to { transform: rotate(360deg); } }
        .av-scroll::-webkit-scrollbar { display: none; }
        .av-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        .av-tab-btn {
          font-family: ${MONO};
          font-size: 11px; font-weight: 500;
          letter-spacing: 0.06em; text-transform: uppercase;
          padding: 5px 14px;
          border: none; background: none;
          color: #9ca3af; cursor: pointer;
          border-radius: 6px;
          transition: all 0.12s;
        }
        .av-tab-btn:hover { color: #374151; background: #f3f4f6; }
        .av-tab-btn.active { background: #111827; color: #fff; }
      `}</style>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          background: T.bgPage,
          fontFamily: FF,
          overflow: "hidden",
        }}
      >
        {status === "loading" && <LoadingState />}
        {status === "not_found" && <NotFound />}

        {status === "found" && applicant && (
          <div className="av-scroll" style={{ flex: 1, overflowY: "auto" }}>
            {/* ── Column 1: Profile ── */}
            <div
              ref={containerRef}
              className="av-scroll"
              style={{
                flex: 1,
                overflowY: "auto",
                padding: "20px 20px 40px",
                background: T.bgPage,
              }}
            >
              {/* Profile Header */}
              <div
                style={{
                  background: T.bg,
                  padding: 24,
                  marginBottom: 12,
                  borderRadius: 0,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "flex-start", gap: 20 }}
                >
                  {/* Avatar */}
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      color: "#fff",
                      fontSize: 24,
                      fontWeight: 700,
                      fontFamily: FF,
                    }}
                  >
                    {(user?.name ??
                      applicant?.applicantName ??
                      "?")[0].toUpperCase()}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h1
                      style={{
                        fontSize: 20,
                        fontWeight: 600,
                        color: T.text,
                        marginBottom: 3,
                        lineHeight: 1.3,
                      }}
                    >
                      {user?.name ?? applicant?.applicantName ?? "Unknown"}
                    </h1>
                    <p
                      style={{
                        fontSize: 14,
                        color: T.textTertiary,
                        marginBottom: 10,
                      }}
                    >
                      {user?.personal_information?.currentJobTitle ??
                        applicant?.personal_information?.currentJobTitle ??
                        "Position not specified"}
                      {(user?.personal_information?.currentCompany ??
                        applicant?.personal_information?.currentCompany) && (
                        <>
                          <span style={{ color: "#d1d5db", margin: "0 5px" }}>
                            at
                          </span>
                          <span style={{ color: T.text }}>
                            {user?.personal_information?.currentCompany ??
                              applicant?.personal_information?.currentCompany}
                          </span>
                        </>
                      )}
                    </p>

                    {/* Badges */}
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 6,
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "4px 10px",
                          background: "#f3f4f6",
                          borderRadius: 6,
                        }}
                      >
                        <Briefcase
                          size={12}
                          style={{ color: T.textTertiary }}
                        />
                        <span style={{ fontSize: 12, color: T.textTertiary }}>
                          {user?.personal_information?.yearsExperience ??
                            applicant?.personal_information?.yearsExperience ??
                            "N/A"}{" "}
                          years
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "4px 10px",
                          background: "#eff6ff",
                          borderRadius: 6,
                        }}
                      >
                        <Clock size={12} style={{ color: "#2563eb" }} />
                        <span style={{ fontSize: 12, color: "#1d4ed8" }}>
                          {user?.job_preferences?.noticePeriod ??
                            applicant?.job_preferences?.noticePeriod ??
                            "Not specified"}{" "}
                          notice
                        </span>
                      </div>
                    </div>

                    {/* Phone */}
                    {(user?.phone ?? applicant?.phone) && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginBottom: 12,
                        }}
                      >
                        <Phone size={13} style={{ color: "#9ca3af" }} />
                        <span style={{ fontSize: 13, color: T.textTertiary }}>
                          {user?.phone ?? applicant?.phone}
                        </span>
                      </div>
                    )}

                    {/* Social links */}
                    <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
                      {[
                        {
                          label: "Li",
                          alt: "LinkedIn",
                          link: user?.social_links?.linkedin,
                        },
                        {
                          label: "Gh",
                          alt: "GitHub",
                          link: user?.social_links?.github,
                        },
                        {
                          label: "Lc",
                          alt: "LeetCode",
                          link: user?.social_links?.leetcode,
                        },
                        {
                          label: "Wb",
                          alt: "Portfolio",
                          link: user?.social_links?.portfolio,
                        },
                      ].map((s, i) => (
                        <button
                          key={i}
                          onClick={() =>
                            s.link && window.open(s.link, "_blank")
                          }
                          title={s.alt}
                          disabled={!s.link}
                          style={{
                            padding: 7,
                            borderRadius: 8,
                            background: "#f9fafb",
                            border: "1px solid #e5e7eb",
                            cursor: s.link ? "pointer" : "not-allowed",
                            opacity: s.link ? 1 : 0.4,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <SocialIcon label={s.alt} />
                        </button>
                      ))}
                    </div>

                    {/* Download resume only (no message/archive) */}
                    {applicant?.resume && (
                      <button
                        onClick={() => {
                          const a = document.createElement("a")
                          a.href = applicant.resume.resumeUrl
                          a.download = applicant.resume.fileName ?? "resume.pdf"
                          a.click()
                        }}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "8px 16px",
                          background: T.bg,
                          border: "1px solid #e5e7eb",
                          borderRadius: 100,
                          fontSize: 13,
                          fontWeight: 500,
                          color: T.textMuted,
                          cursor: "pointer",
                        }}
                      >
                        <Download size={14} />
                        Resume
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Tab toggle */}
              <div
                style={{
                  background: T.bg,
                  padding: "10px 16px",
                  marginBottom: 12,
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <button
                  className={`av-tab-btn${profileTab === "parsed" ? " active" : ""}`}
                  onClick={() => setProfileTab("parsed")}
                >
                  Parsed
                </button>
                <button
                  className={`av-tab-btn${profileTab === "original" ? " active" : ""}`}
                  onClick={() => setProfileTab("original")}
                >
                  Original
                </button>
              </div>

              {/* ── Original: PDF ── */}
              {profileTab === "original" && (
                <div style={{ background: T.bg, overflow: "hidden" }}>
                  <div
                    style={{
                      padding: "16px 20px 32px",
                      background: "#fafafa",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    {resumeUrl ? (
                      <Document
                        file={resumeUrl}
                        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                        loading={
                          <div
                            style={{
                              width: pdfWidth,
                              aspectRatio: "1/1.414",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 10,
                            }}
                          >
                            <Loader2
                              size={16}
                              style={{
                                color: "#a1a1aa",
                                animation: "av-spin 0.8s linear infinite",
                              }}
                            />
                            <p
                              style={{
                                color: "#a1a1aa",
                                fontSize: 11,
                                fontFamily: MONO,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                margin: 0,
                              }}
                            >
                              Loading Resume
                            </p>
                          </div>
                        }
                        error={
                          <p
                            style={{
                              color: "#a1a1aa",
                              fontSize: 11,
                              fontFamily: MONO,
                              letterSpacing: "0.1em",
                              textTransform: "uppercase",
                              margin: 0,
                            }}
                          >
                            Could not load resume
                          </p>
                        }
                      >
                        {Array.from({ length: numPages || 0 }, (_, i) => (
                          <div
                            key={i}
                            style={{
                              marginBottom: i < (numPages || 1) - 1 ? 12 : 0,
                              border: "1px solid #e8e8e8",
                              borderRadius: 4,
                              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                              lineHeight: 0,
                              background: "#fff",
                            }}
                          >
                            <Page
                              pageNumber={i + 1}
                              width={pdfWidth}
                              renderAnnotationLayer={false}
                              renderTextLayer={false}
                            />
                          </div>
                        ))}
                      </Document>
                    ) : (
                      <p
                        style={{
                          color: "#a1a1aa",
                          fontSize: 11,
                          fontFamily: MONO,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          margin: 0,
                        }}
                      >
                        No resume attached
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* ── Parsed: profile sections ── */}
              {profileTab === "parsed" && (
                <>
                  {/* Work Experience */}
                  {user?.work_history?.length > 0 && (
                    <div
                      style={{
                        background: T.bg,
                        padding: "22px 24px",
                        marginBottom: 12,
                      }}
                    >
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: T.text,
                          marginBottom: 18,
                        }}
                      >
                        Work Experience
                      </h2>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 22,
                        }}
                      >
                        {user.work_history.map((w, i) => (
                          <div key={i} style={{ display: "flex", gap: 12 }}>
                            <div
                              style={{
                                width: 38,
                                height: 38,
                                borderRadius: 8,
                                background: "#f3f4f6",
                                border: "1px solid #f3f4f6",
                                flexShrink: 0,
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  justifyContent: "space-between",
                                  marginBottom: 2,
                                }}
                              >
                                <div>
                                  <p
                                    style={{
                                      fontSize: 14,
                                      fontWeight: 600,
                                      color: T.text,
                                    }}
                                  >
                                    {w.role ?? "Position"}
                                  </p>
                                  <p
                                    style={{
                                      fontSize: 13,
                                      color: T.textTertiary,
                                    }}
                                  >
                                    {w.companyName ?? "Company"}
                                  </p>
                                </div>
                                <p
                                  style={{
                                    fontSize: 12,
                                    color: "#9ca3af",
                                    flexShrink: 0,
                                    marginLeft: 12,
                                  }}
                                >
                                  {w.startMonth && w.startYear
                                    ? `${w.startMonth} ${w.startYear} – ${w.isCurrentJob || (!w.endMonth && !w.endYear) ? "Present" : `${w.endMonth} ${w.endYear}`}`
                                    : "Present"}
                                </p>
                              </div>
                              {w.description && (
                                <p
                                  style={{
                                    fontSize: 14,
                                    color: "#4b5563",
                                    lineHeight: 1.65,
                                    marginTop: 6,
                                    whiteSpace: "pre-line",
                                  }}
                                >
                                  {w.description}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Education */}
                  {user?.education?.length > 0 && (
                    <div
                      style={{
                        background: T.bg,
                        padding: "22px 24px",
                        marginBottom: 12,
                      }}
                    >
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: T.text,
                          marginBottom: 18,
                        }}
                      >
                        Education
                      </h2>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 16,
                        }}
                      >
                        {user.education.map((e, i) => (
                          <div key={i} style={{ display: "flex", gap: 12 }}>
                            <div
                              style={{
                                width: 38,
                                height: 38,
                                borderRadius: 8,
                                background: "#f3f4f6",
                                border: "1px solid #f3f4f6",
                                flexShrink: 0,
                              }}
                            />
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  justifyContent: "space-between",
                                }}
                              >
                                <p
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: T.text,
                                  }}
                                >
                                  {e.degree}
                                  {e.field && (
                                    <span
                                      style={{
                                        fontWeight: 400,
                                        color: T.textTertiary,
                                      }}
                                    >
                                      {", "}
                                      {e.field}
                                    </span>
                                  )}
                                </p>
                                <p
                                  style={{
                                    fontSize: 12,
                                    color: "#9ca3af",
                                    flexShrink: 0,
                                    marginLeft: 12,
                                  }}
                                >
                                  {e.startMonth &&
                                  e.startYear &&
                                  e.endMonth &&
                                  e.endYear
                                    ? `${e.startMonth} ${e.startYear} – ${e.endMonth} ${e.endYear}`
                                    : (e.graduationYear ?? "")}
                                </p>
                              </div>
                              <p
                                style={{
                                  fontSize: 13,
                                  color: T.textTertiary,
                                  marginTop: 2,
                                }}
                              >
                                {e.institute}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Projects */}
                  {user?.projects?.length > 0 && (
                    <div
                      style={{
                        background: T.bg,
                        padding: "22px 24px",
                        marginBottom: 12,
                      }}
                    >
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: T.text,
                          marginBottom: 18,
                        }}
                      >
                        Projects
                      </h2>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 18,
                        }}
                      >
                        {user.projects.map((p, i) => (
                          <div key={i}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                marginBottom: 4,
                              }}
                            >
                              <p
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: "#374151",
                                }}
                              >
                                {p.title ?? "Project"}
                              </p>
                              {p.url && (
                                <button
                                  onClick={() =>
                                    window.open(
                                      p.url,
                                      "_blank",
                                      "noopener,noreferrer",
                                    )
                                  }
                                  style={{
                                    color: "#9ca3af",
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    flexShrink: 0,
                                    display: "flex",
                                  }}
                                >
                                  <ExternalLink size={15} />
                                </button>
                              )}
                            </div>
                            <p
                              style={{
                                fontSize: 14,
                                color: "#4b5563",
                                lineHeight: 1.65,
                                marginBottom: 4,
                              }}
                            >
                              {p.description}
                            </p>
                            {p.url && (
                              <button
                                onClick={() =>
                                  window.open(
                                    p.url,
                                    "_blank",
                                    "noopener,noreferrer",
                                  )
                                }
                                style={{
                                  fontSize: 13,
                                  color: "#2563eb",
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: 0,
                                }}
                              >
                                {p.url}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skills */}
                  {user?.resumeData?.skills?.length > 0 && (
                    <div
                      style={{
                        background: T.bg,
                        padding: "22px 24px",
                        marginBottom: 12,
                      }}
                    >
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: T.text,
                          marginBottom: 14,
                        }}
                      >
                        Skills
                      </h2>
                      <div
                        style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
                      >
                        {(showAllSkills
                          ? user.resumeData.skills
                          : user.resumeData.skills.slice(0, 15)
                        ).map((s, i) => (
                          <span
                            key={i}
                            style={{
                              padding: "5px 12px",
                              background: "#f3f4f6",
                              color: "#374151",
                              fontSize: 13,
                              borderRadius: 100,
                            }}
                          >
                            {s}
                          </span>
                        ))}
                        {user.resumeData.skills.length > 15 && (
                          <button
                            onClick={() => setShowAllSkills(!showAllSkills)}
                            style={{
                              padding: "5px 12px",
                              fontSize: 13,
                              fontWeight: 500,
                              color: "#2563eb",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              borderRadius: 100,
                            }}
                          >
                            {showAllSkills
                              ? "Show less"
                              : `+${user.resumeData.skills.length - 15} more`}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Achievements */}
                  {user?.achievements?.length > 0 && (
                    <div
                      style={{
                        background: T.bg,
                        padding: "22px 24px",
                        marginBottom: 12,
                      }}
                    >
                      <h2
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: T.text,
                          marginBottom: 14,
                        }}
                      >
                        Achievements
                      </h2>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 12,
                        }}
                      >
                        {user.achievements.map((a, i) => (
                          <div key={i} style={{ display: "flex", gap: 10 }}>
                            <div style={{ flexShrink: 0, marginTop: 2 }}>
                              <div
                                style={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  background:
                                    "linear-gradient(135deg,#fbbf24,#f97316)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <Award size={13} style={{ color: "#fff" }} />
                              </div>
                            </div>
                            <div style={{ flex: 1 }}>
                              {a.title && (
                                <p
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: T.text,
                                    marginBottom: 2,
                                  }}
                                >
                                  {a.title}
                                </p>
                              )}
                              <p
                                style={{
                                  fontSize: 14,
                                  color: a.title ? T.textTertiary : T.text,
                                  lineHeight: 1.6,
                                }}
                              >
                                {a.achievement}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Fallback: if no user profile sections rendered */}
                  {!user?.work_history?.length &&
                    !user?.education?.length &&
                    !user?.projects?.length &&
                    !user?.resumeData?.skills?.length && (
                      <div
                        style={{
                          background: T.bg,
                          padding: "32px 24px",
                          textAlign: "center",
                        }}
                      >
                        <p
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: T.textTertiary,
                            fontFamily: FF,
                          }}
                        >
                          No parsed profile data available
                        </p>
                      </div>
                    )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ApplicantViewer
